/**
 * DP / Folha — Fase 3.3: Geração de Remessa Bancária CNAB 240
 * Aba do DPFolhaConfigPage.
 *
 * Sub-tabs:
 *   - Configurações Bancárias (CRUD)
 *   - Gerar Remessa (a partir de competência calculada)
 *   - Histórico de Remessas (lista + download .rem)
 */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Landmark, Plus, Pencil, Trash2, Download, FileText, Loader2, AlertTriangle, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtBRL = (v) => v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (s) => s ? String(s).slice(0, 10).split('-').reverse().join('/') : '—';
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// ============================================================================
// SUB-TAB: CONFIGURAÇÕES BANCÁRIAS
// ============================================================================
const ConfiguracoesTab = () => {
  const [items, setItems] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = {
    empresa_id: '', banco_codigo: '341', agencia: '', agencia_dv: '',
    conta: '', conta_dv: '', codigo_convenio: '', codigo_empresa: '',
    descricao: '', ativo: true,
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/api/calculos-dp/cnab240/configuracoes`, authHeader()),
        axios.get(`${API}/api/calculos-dp/cnab240/bancos`, authHeader()),
        axios.get(`${API}/api/companies`, authHeader()),
      ]);
      setItems(r1.data || []);
      setBancos(r2.data || []);
      setEmpresas(r3.data || []);
    } catch (e) { toast.error('Erro ao carregar'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.empresa_id || !form.agencia || !form.conta || !form.conta_dv || !form.codigo_convenio) {
      return toast.error('Empresa, agência, conta+DV e convênio são obrigatórios');
    }
    try {
      if (edit) {
        await axios.put(`${API}/api/calculos-dp/cnab240/configuracoes/${edit}`, form, authHeader());
      } else {
        await axios.post(`${API}/api/calculos-dp/cnab240/configuracoes`, form, authHeader());
      }
      toast.success('Configuração salva');
      setOpen(false); setForm(empty); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir esta configuração?')) return;
    await axios.delete(`${API}/api/calculos-dp/cnab240/configuracoes/${id}`, authHeader());
    toast.success('Excluída'); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">{items.length} configuração(ões) cadastrada(s).</p>
        <Button onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} data-testid="cnab-cfg-novo-btn">
          <Plus className="h-4 w-4 mr-2" /> Nova Configuração
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Empresa</th>
              <th className="text-left p-3">Banco</th>
              <th className="text-left p-3">Agência</th>
              <th className="text-left p-3">Conta</th>
              <th className="text-left p-3">Convênio</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="p-4 text-center text-slate-500">Nenhuma configuração cadastrada.</td></tr>
            ) : items.map((c) => (
              <tr key={c.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-medium">{c.empresa_nome}</td>
                <td className="p-3"><Badge variant="outline">{c.banco_codigo}</Badge> {c.banco_nome}</td>
                <td className="p-3">{c.agencia}{c.agencia_dv ? `-${c.agencia_dv}` : ''}</td>
                <td className="p-3">{c.conta}-{c.conta_dv}</td>
                <td className="p-3 font-mono text-xs">{c.codigo_convenio}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(c.id); setForm({ ...empty, ...c }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="cnab-cfg-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? 'Editar Configuração' : 'Nova Configuração CNAB 240'}</DialogTitle>
            <DialogDescription>Dados de conta para envio de remessa bancária. O código do convênio é fornecido pelo seu banco no contrato de Folha de Pagamento.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Empresa *</Label>
              <Select value={form.empresa_id} onValueChange={(v) => setForm({ ...form, empresa_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {empresas.map((e) => (<SelectItem key={e.id} value={e.id}>{e.name || e.razao_social || e.id}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Banco *</Label>
              <Select value={form.banco_codigo} onValueChange={(v) => setForm({ ...form, banco_codigo: v })}>
                <SelectTrigger data-testid="cnab-cfg-banco"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {bancos.map((b) => (<SelectItem key={b.codigo} value={b.codigo}>{b.codigo} — {b.nome}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Código Convênio *</Label><Input value={form.codigo_convenio} onChange={(e) => setForm({ ...form, codigo_convenio: e.target.value.replace(/\D/g, '') })} placeholder="varia por banco" inputMode="numeric" /></div>
            <div><Label>Agência *</Label><Input value={form.agencia} onChange={(e) => setForm({ ...form, agencia: e.target.value.replace(/\D/g, '') })} inputMode="numeric" /></div>
            <div><Label>DV Agência</Label><Input maxLength={1} value={form.agencia_dv} onChange={(e) => setForm({ ...form, agencia_dv: e.target.value.replace(/\D/g, '') })} inputMode="numeric" /></div>
            <div><Label>Conta *</Label><Input value={form.conta} onChange={(e) => setForm({ ...form, conta: e.target.value.replace(/\D/g, '') })} inputMode="numeric" /></div>
            <div><Label>DV Conta *</Label><Input maxLength={1} value={form.conta_dv} onChange={(e) => setForm({ ...form, conta_dv: e.target.value.replace(/\D/g, '') })} inputMode="numeric" /></div>
            <div className="col-span-2"><Label>Código Empresa no Banco</Label><Input value={form.codigo_empresa} onChange={(e) => setForm({ ...form, codigo_empresa: e.target.value })} placeholder="ex.: Itaú 6 dígitos" /></div>
            <div className="col-span-2"><Label>Descrição</Label><Textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></div>
            <div className="col-span-2 flex items-center gap-2">
              <Switch checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} />
              <Label>Ativa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="cnab-cfg-salvar-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// SUB-TAB: GERAR REMESSA
// ============================================================================
const GerarRemessaTab = ({ onGerada }) => {
  const [configs, setConfigs] = useState([]);
  const [competencias, setCompetencias] = useState([]);
  const [formas, setFormas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ competencia_id: '', configuracao_id: '', forma_pagamento: '01', data_pagamento: '', historico: 'FOLHA DE PAGAMENTO', incluir_pj: false });
  const [resultado, setResultado] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/api/calculos-dp/cnab240/configuracoes`, authHeader()),
        axios.get(`${API}/api/calculos-dp/competencias`, authHeader()),
        axios.get(`${API}/api/calculos-dp/cnab240/formas-pagamento`, authHeader()),
      ]);
      setConfigs(r1.data || []);
      setCompetencias((r2.data || []).filter(c => c.status === 'calculada' || c.status === 'fechada'));
      setFormas(r3.data || []);
    } catch { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const gerar = async () => {
    if (!form.competencia_id || !form.configuracao_id || !form.data_pagamento) {
      return toast.error('Competência, configuração e data de pagamento obrigatórios');
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/calculos-dp/cnab240/gerar`, form, authHeader());
      setResultado(r.data);
      toast.success(`✅ Remessa gerada · ${r.data.qtd_pagamentos} pagamentos · ${fmtBRL(r.data.valor_total)}`, { duration: 6000 });
      if (onGerada) onGerada();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao gerar'); }
    finally { setLoading(false); }
  };

  const baixar = async () => {
    if (!resultado?.remessa_id) return;
    const r = await axios.get(`${API}/api/calculos-dp/cnab240/remessas/${resultado.remessa_id}/download`, {
      ...authHeader(), responseType: 'blob'
    });
    const url = URL.createObjectURL(new Blob([r.data], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url; a.download = resultado.nome_arquivo; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-indigo-600" />Gerar Arquivo CNAB 240</CardTitle>
          <CardDescription>Gera o arquivo de remessa bancária (FEBRABAN v10.7) com os pagamentos da competência selecionada, no formato exigido pelo banco.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          <div>
            <Label>Competência *</Label>
            <Select value={form.competencia_id} onValueChange={(v) => setForm({ ...form, competencia_id: v })}>
              <SelectTrigger data-testid="cnab-gerar-comp"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                {competencias.length === 0 ? (
                  <SelectItem value="_" disabled>Nenhuma competência calculada</SelectItem>
                ) : competencias.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{MESES[c.mes - 1]}/{c.ano} — {c.tipo} ({fmtBRL(c.totais?.liquido)})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Configuração Bancária *</Label>
            <Select value={form.configuracao_id} onValueChange={(v) => setForm({ ...form, configuracao_id: v })}>
              <SelectTrigger data-testid="cnab-gerar-cfg"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                {configs.length === 0 ? (
                  <SelectItem value="_" disabled>Nenhuma configuração cadastrada</SelectItem>
                ) : configs.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.banco_nome} · Ag {c.agencia} · Cc {c.conta}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Forma de Pagamento *</Label>
            <Select value={form.forma_pagamento} onValueChange={(v) => setForm({ ...form, forma_pagamento: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {formas.map((f) => (<SelectItem key={f.codigo} value={f.codigo}>{f.codigo} — {f.descricao}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Data de Pagamento *</Label><Input type="date" value={form.data_pagamento} onChange={(e) => setForm({ ...form, data_pagamento: e.target.value })} data-testid="cnab-gerar-data" /></div>
          <div className="col-span-2"><Label>Histórico (40 caracteres)</Label><Input maxLength={40} value={form.historico} onChange={(e) => setForm({ ...form, historico: e.target.value })} /></div>
          <div className="col-span-2 flex items-center gap-2">
            <Switch checked={form.incluir_pj} onCheckedChange={(v) => setForm({ ...form, incluir_pj: v })} id="incluir-pj" />
            <Label htmlFor="incluir-pj">Incluir pagamentos PJ na remessa</Label>
          </div>
          <div className="col-span-2">
            <Button onClick={gerar} disabled={loading} className="w-full" data-testid="cnab-gerar-btn">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Gerando...</> : <><Send className="h-4 w-4 mr-2" /> Gerar Remessa</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {resultado && (
        <Card className="border-emerald-300 bg-emerald-50/50">
          <CardHeader>
            <CardTitle className="text-emerald-700 text-base">✅ Remessa gerada com sucesso</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div><b>Arquivo:</b> <code className="bg-white px-2 py-1 rounded">{resultado.nome_arquivo}</code></div>
            <div><b>Banco:</b> {resultado.banco}</div>
            <div><b>Pagamentos:</b> {resultado.qtd_pagamentos}</div>
            <div><b>Valor total:</b> {fmtBRL(resultado.valor_total)}</div>
            {resultado.ignorados?.length > 0 && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                <div className="flex items-center gap-2 text-amber-700 font-medium"><AlertTriangle className="h-4 w-4" /> {resultado.ignorados.length} colaborador(es) ignorado(s)</div>
                <ul className="text-xs mt-1 list-disc list-inside text-amber-800">
                  {resultado.ignorados.map((i, idx) => <li key={idx}>{i.nome}: {i.motivo}</li>)}
                </ul>
              </div>
            )}
            <Button onClick={baixar} className="mt-2" data-testid="cnab-gerar-download-btn"><Download className="h-4 w-4 mr-2" /> Baixar arquivo .rem</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============================================================================
// SUB-TAB: HISTÓRICO
// ============================================================================
const HistoricoTab = ({ refreshKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/cnab240/remessas`, authHeader());
      setItems(r.data || []);
    } catch { /* */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const baixar = async (rem) => {
    const r = await axios.get(`${API}/api/calculos-dp/cnab240/remessas/${rem.id}/download`, { ...authHeader(), responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([r.data], { type: 'text/plain' }));
    const a = document.createElement('a'); a.href = url; a.download = rem.nome_arquivo; a.click();
    URL.revokeObjectURL(url);
  };
  const remove = async (id) => {
    if (!window.confirm('Excluir esta remessa do histórico?')) return;
    await axios.delete(`${API}/api/calculos-dp/cnab240/remessas/${id}`, authHeader());
    toast.success('Excluída'); load();
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{items.length} remessa(s) geradas.</p>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Arquivo</th>
              <th className="text-left p-3">Banco</th>
              <th className="text-left p-3">Data Pagto</th>
              <th className="text-right p-3">Pagamentos</th>
              <th className="text-right p-3">Valor Total</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-4 text-center"><Loader2 className="h-4 w-4 inline animate-spin" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="p-4 text-center text-slate-500">Nenhuma remessa gerada ainda.</td></tr>
            ) : items.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-mono text-xs">{r.nome_arquivo}</td>
                <td className="p-3">{r.banco_nome}</td>
                <td className="p-3">{fmtDate(r.data_pagamento)}</td>
                <td className="p-3 text-right">{r.qtd_pagamentos}</td>
                <td className="p-3 text-right font-semibold">{fmtBRL(r.valor_total)}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => baixar(r)} data-testid={`cnab-hist-download-${r.id}`}><Download className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// EXPORT
// ============================================================================
const Cnab240Tab = () => {
  const [sub, setSub] = useState('config');
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <Tabs value={sub} onValueChange={setSub}>
      <TabsList className="grid grid-cols-3 mb-4">
        <TabsTrigger value="config" data-testid="cnab-tab-config"><Landmark className="h-4 w-4 mr-1" />Configurações Bancárias</TabsTrigger>
        <TabsTrigger value="gerar" data-testid="cnab-tab-gerar"><Send className="h-4 w-4 mr-1" />Gerar Remessa</TabsTrigger>
        <TabsTrigger value="historico" data-testid="cnab-tab-historico"><FileText className="h-4 w-4 mr-1" />Histórico</TabsTrigger>
      </TabsList>
      <TabsContent value="config"><ConfiguracoesTab /></TabsContent>
      <TabsContent value="gerar"><GerarRemessaTab onGerada={() => setRefreshKey(k => k + 1)} /></TabsContent>
      <TabsContent value="historico"><HistoricoTab refreshKey={refreshKey} /></TabsContent>
    </Tabs>
  );
};

export default Cnab240Tab;
